import React, { useState, useEffect } from 'react';
import { Bold, Italic, Link, Image, Eye, Edit, Plus, CornerDownLeft, Type, Hash, HashIcon, List, ListOrdered, Table, Video, Music, FileImage, X, Upload, Search, Sparkles, ShoppingCart, Gamepad2, Sigma, SquareStack } from 'lucide-react';
import ImageUploadModal from './ImageUploadModal';
import StockImageModal from './StockImageModal';
import AIImageModal from './AIImageModal';
import AITextEditModal from './AITextEditModal';
import AmazonProductCard from './AmazonProductCard';
import { formatSegmentLabel } from '@shared/statistic-segment';
import { withMathMarkdown, MATH_SNIPPETS, type MathSnippet } from '@shared/math-markdown';
import { areAmazonAffiliatesEnabled, wrapInAffiliateBlock } from '@shared/amazon-affiliates';

// Helper function to detect if image_icon is SVG content
const isSvgContent = (imageIcon: string): boolean => {
  return imageIcon.startsWith('data:image/svg+xml') || 
         imageIcon.startsWith('<svg') || 
         imageIcon.includes('<?xml') && imageIcon.includes('<svg');
};

// Helper function to extract SVG content from data URL
const extractSvgContent = (dataUrl: string): string => {
  if (dataUrl.startsWith('data:image/svg+xml;base64,')) {
    try {
      const base64 = dataUrl.split(',')[1];
      return atob(base64);
    } catch (e) {
      console.error('Error decoding base64 SVG:', e);
      return '';
    }
  } else if (dataUrl.startsWith('data:image/svg+xml,')) {
    return decodeURIComponent(dataUrl.split(',')[1]);
  }
  return dataUrl;
};

// Component to render SVG with transparent background processing
const SvgIconRenderer: React.FC<{ svgContent: string; className?: string }> = ({ svgContent, className = "" }) => {
  if (!svgContent) {
    return (
      <div className={`flex items-center justify-center text-muted-foreground ${className}`}>
        <X size={24} />
      </div>
    );
  }

  // Process the SVG to ensure proper scaling and remove background
  let processedSvg = svgContent
    // Remove any background elements (rect or path with white/light fills)
    .replace(/<(rect|path)[^>]*fill\s*=\s*["']rgb\(254,254,254\)["'][^>]*\/?>/gi, '')
    .replace(/<(rect|path)[^>]*fill\s*=\s*["']#ffffff["'][^>]*\/?>/gi, '')
    .replace(/<(rect|path)[^>]*fill\s*=\s*["']white["'][^>]*\/?>/gi, '')
    .replace(/<(rect|path)[^>]*fill\s*=\s*["']rgb\(255,255,255\)["'][^>]*\/?>/gi, '')
    .replace(/<(rect|path)[^>]*fill\s*=\s*["']rgb\(255,255,254\)["'][^>]*\/?>/gi, '')
    .replace(/<(rect|path)[^>]*fill\s*=\s*["']rgb\(254,255,255\)["'][^>]*\/?>/gi, '')
    // Remove any background style attributes
    .replace(/\s*background\s*:\s*[^;"]*;?/gi, '')
    .replace(/\s*background-color\s*:\s*[^;"]*;?/gi, '')
    .replace(
      /<svg([^>]*)>/i, 
      (match, attributes) => {
        // Extract existing attributes
        const hasViewBox = /viewBox=/i.test(attributes);
        
        // Remove existing width and height if they exist
        let cleanAttributes = attributes
          .replace(/\s*width\s*=\s*["'][^"']*["']/gi, '')
          .replace(/\s*height\s*=\s*["'][^"']*["']/gi, '');
        
        // Add viewBox if it doesn't exist (assume 1024x1024 for Replicate SVGs)
        if (!hasViewBox) {
          cleanAttributes += ' viewBox="0 0 1024 1024"';
        }
        
        // Add responsive styling with transparent background (same as PNG icons)
        const style = 'style="width: 100%; height: 100%; background: transparent;"';
        
        return `<svg${cleanAttributes} ${style}>`;
      }
    );

  return (
    <div className={`flex items-center justify-center overflow-hidden w-10 h-10 flex-shrink-0 ${className}`}>
      <div 
        dangerouslySetInnerHTML={{ __html: processedSvg }}
        className="w-full h-full object-contain"
      />
    </div>
  );
};

interface StatisticBlock {
  id: string;
  title: string;
  value: number;
  unit?: string;
  source?: string;
  source_url?: string;
  type: 'extracted' | 'estimation' | 'calculated' | 'assumption';
  description: string;
  formula?: string;
  image_icon?: string;
  hidden?: boolean;
}

interface Experience {
  id: string;
  title: string;
  description?: string;
  type: 'game' | 'calculator' | 'visualization' | 'quiz' | 'simulator' | 'other';
}

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  charts?: Array<{ id: string; title: string }>;
  highlights?: Array<{ id: string; text: string }>;
  segments?: Array<{ dimension: string; dimension_title?: string; description: string; values: Record<string, number> }>;
  facts?: StatisticBlock[];
  amazonProducts?: Array<{ id: string; asin: string; title: string; price?: string; imageUrl?: string }>;
  experiences?: Experience[];
  className?: string;
  fullscreen?: boolean;
  articleField?: 'description' | 'conclusion' | string;
  statisticTitle?: string;
}

const ARTICLE_FIELD_LABELS: Record<string, string> = {
  description: 'Descripción',
  conclusion: 'Conclusiones',
};

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  placeholder = "Escribe tu descripción aquí...",
  charts = [],
  highlights = [],
  segments = [],
  facts = [],
  amazonProducts = [],
  experiences = [],
  className = "",
  fullscreen = false,
  articleField = 'description',
  statisticTitle = ''
}) => {
  const [isPreview, setIsPreview] = useState(false);
  const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);
  const [showChartMenu, setShowChartMenu] = useState(false);
  const [showHighlightMenu, setShowHighlightMenu] = useState(false);
  const [showSegmentMenu, setShowSegmentMenu] = useState(false);
  const [showFactMenu, setShowFactMenu] = useState(false);
  const [showAmazonProductMenu, setShowAmazonProductMenu] = useState(false);
  const [showExperienceMenu, setShowExperienceMenu] = useState(false);
  const [showMathMenu, setShowMathMenu] = useState(false);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showAITextModal, setShowAITextModal] = useState(false);
  const [aiEditContext, setAiEditContext] = useState<{
    start: number;
    end: number;
    selectedText: string;
  } | null>(null);

  // Save selection when text changes
  useEffect(() => {
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (textarea) {
      const updateSelection = () => {
        setSelection({
          start: textarea.selectionStart,
          end: textarea.selectionEnd
        });
      };
      textarea.addEventListener('select', updateSelection);
      textarea.addEventListener('click', updateSelection);
      textarea.addEventListener('keyup', updateSelection);
      return () => {
        textarea.removeEventListener('select', updateSelection);
        textarea.removeEventListener('click', updateSelection);
        textarea.removeEventListener('keyup', updateSelection);
      };
    }
  }, [isPreview]);

  // Close chart menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.chart-menu-container')) {
        setShowChartMenu(false);
      }
      if (!target.closest('.highlight-menu-container')) {
        setShowHighlightMenu(false);
      }
      if (!target.closest('.segment-menu-container')) {
        setShowSegmentMenu(false);
      }
      if (!target.closest('.fact-menu-container')) {
        setShowFactMenu(false);
      }
      if (!target.closest('.amazon-menu-container')) {
        setShowAmazonProductMenu(false);
      }
      if (!target.closest('.experience-menu-container')) {
        setShowExperienceMenu(false);
      }
      if (!target.closest('.math-menu-container')) {
        setShowMathMenu(false);
      }
      // Close source tooltips when clicking outside
      if (!target.closest('.tooltip-source') && !target.closest('button[onclick*="tooltip"]')) {
        document.querySelectorAll('.tooltip-source').forEach(tooltip => {
          tooltip.classList.add('opacity-0');
          tooltip.classList.remove('opacity-100');
        });
      }
    };

    if (showChartMenu || showHighlightMenu || showSegmentMenu || showFactMenu || showAmazonProductMenu || showExperienceMenu || showMathMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showChartMenu, showHighlightMenu, showSegmentMenu, showFactMenu, showAmazonProductMenu, showExperienceMenu, showMathMenu]);

  // Process SVG elements after HTML is rendered
  useEffect(() => {
    if (isPreview) {
      // Find all elements with data-svg-content attribute
      const svgElements = document.querySelectorAll('[data-svg-content]');
      svgElements.forEach(element => {
        const svgContent = decodeURIComponent(element.getAttribute('data-svg-content') || '');
        if (svgContent) {
          // Process the SVG content
          let processedSvg = svgContent
            // Remove any background elements (rect or path with white/light fills)
            .replace(/<(rect|path)[^>]*fill\s*=\s*["']rgb\(254,254,254\)["'][^>]*\/?>/gi, '')
            .replace(/<(rect|path)[^>]*fill\s*=\s*["']#ffffff["'][^>]*\/?>/gi, '')
            .replace(/<(rect|path)[^>]*fill\s*=\s*["']white["'][^>]*\/?>/gi, '')
            .replace(/<(rect|path)[^>]*fill\s*=\s*["']rgb\(255,255,255\)["'][^>]*\/?>/gi, '')
            .replace(/<(rect|path)[^>]*fill\s*=\s*["']rgb\(255,255,254\)["'][^>]*\/?>/gi, '')
            .replace(/<(rect|path)[^>]*fill\s*=\s*["']rgb\(254,255,255\)["'][^>]*\/?>/gi, '')
            // Remove any background style attributes
            .replace(/\s*background\s*:\s*[^;"]*;?/gi, '')
            .replace(/\s*background-color\s*:\s*[^;"]*;?/gi, '')
            .replace(
              /<svg([^>]*)>/i, 
              (match, attributes) => {
                // Extract existing attributes
                const hasViewBox = /viewBox=/i.test(attributes);
                
                // Remove existing width and height if they exist
                let cleanAttributes = attributes
                  .replace(/\s*width\s*=\s*["'][^"']*["']/gi, '')
                  .replace(/\s*height\s*=\s*["'][^"']*["']/gi, '');
                
                // Add viewBox if it doesn't exist (assume 1024x1024 for Replicate SVGs)
                if (!hasViewBox) {
                  cleanAttributes += ' viewBox="0 0 1024 1024"';
                }
                
                // Add responsive styling with transparent background (same as PNG icons)
                const style = 'style="width: 100%; height: 100%; background: transparent;"';
                
                return `<svg${cleanAttributes} ${style}>`;
              }
            );

          // Replace the element content with the processed SVG
          element.innerHTML = processedSvg;
          element.removeAttribute('data-svg-content');
        }
      });
    }
  }, [isPreview, value]);

  const openAITextEditor = () => {
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea || isPreview) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    setAiEditContext({
      start,
      end,
      selectedText: value.substring(start, end),
    });
    setShowAITextModal(true);
  };

  const applyAITextEdit = (replacementText: string) => {
    if (!aiEditContext) return;

    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    const { start, end } = aiEditContext;
    const scrollTop = textarea?.scrollTop ?? 0;
    const newText = value.substring(0, start) + replacementText + value.substring(end);
    onChange(newText);

    setTimeout(() => {
      if (!textarea) return;
      textarea.focus();
      textarea.scrollTop = scrollTop;
      const cursorPos = start + replacementText.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  };

  const insertText = (before: string, after: string = '') => {
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const scrollTop = textarea.scrollTop; // Preserve scroll position

    const newText = value.substring(0, start) + before + selectedText + after + value.substring(end);
    onChange(newText);

    // Restore cursor position and scroll
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop; // Restore scroll position
      textarea.setSelectionRange(
        start + before.length,
        start + before.length + selectedText.length
      );
    }, 0);
  };

  const insertLineBreak = () => {
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const textBefore = value.substring(0, cursorPos);
    const textAfter = value.substring(cursorPos);
    const scrollTop = textarea.scrollTop; // Preserve scroll position
    
    // Insert line break at cursor position
    const newText = textBefore + '\n' + textAfter;
    onChange(newText);
    
    // Set cursor position after the inserted line break
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop; // Restore scroll position
      const newCursorPos = cursorPos + 1;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const insertHeader = (level: number) => {
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const textBefore = value.substring(0, cursorPos);
    const textAfter = value.substring(cursorPos);
    const scrollTop = textarea.scrollTop; // Preserve scroll position
    
    // Always insert header at current cursor position without adding line breaks
    const headerText = '#'.repeat(level) + ' ';
    const newText = textBefore + headerText + textAfter;
    onChange(newText);
    
    // Set cursor position after the header
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop; // Restore scroll position
      const newCursorPos = cursorPos + headerText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const insertChart = (chartId: string) => {
    // Get current cursor position
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const textBefore = value.substring(0, cursorPos);
    const textAfter = value.substring(cursorPos);
    const scrollTop = textarea.scrollTop; // Preserve scroll position
    
    // Insert chart at cursor position
    const newText = textBefore + `{{chart:${chartId}}}\n\n` + textAfter;
    onChange(newText);
    
    // Set cursor position after the inserted chart
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop; // Restore scroll position
      const newCursorPos = cursorPos + `{{chart:${chartId}}}\n\n`.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const insertHighlight = (highlightId: string) => {
    // Get current cursor position
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const textBefore = value.substring(0, cursorPos);
    const textAfter = value.substring(cursorPos);
    const scrollTop = textarea.scrollTop; // Preserve scroll position
    
    // Insert highlight at cursor position
    const newText = textBefore + `{{highlight:${highlightId}}}\n\n` + textAfter;
    onChange(newText);
    
    // Set cursor position after the inserted highlight
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop; // Restore scroll position
      const newCursorPos = cursorPos + `{{highlight:${highlightId}}}\n\n`.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const insertSegment = (segmentDimension: string) => {
    // Get current cursor position
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const textBefore = value.substring(0, cursorPos);
    const textAfter = value.substring(cursorPos);
    const scrollTop = textarea.scrollTop; // Preserve scroll position
    
    // Insert segment at cursor position
    const newText = textBefore + `{{segment:${segmentDimension}}}\n\n` + textAfter;
    onChange(newText);
    
    // Set cursor position after the inserted segment
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop; // Restore scroll position
      const newCursorPos = cursorPos + `{{segment:${segmentDimension}}}\n\n`.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const insertFact = (factId: string) => {
    // Get current cursor position
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const textBefore = value.substring(0, cursorPos);
    const textAfter = value.substring(cursorPos);
    const scrollTop = textarea.scrollTop; // Preserve scroll position
    
    // Insert fact at cursor position
    const newText = textBefore + `{{fact:${factId}}}\n\n` + textAfter;
    onChange(newText);
    
    // Set cursor position after the inserted fact
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop; // Preserve scroll position
      const newCursorPos = cursorPos + `{{fact:${factId}}}\n\n`.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const insertAmazonProduct = (productId: string) => {
    // Get current cursor position
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const textBefore = value.substring(0, cursorPos);
    const textAfter = value.substring(cursorPos);
    const scrollTop = textarea.scrollTop; // Preserve scroll position
    
    // Insert Amazon product at cursor position
    const newText = textBefore + `{{amazon:${productId}}}\n\n` + textAfter;
    onChange(newText);
    
    // Set cursor position after the inserted product
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop; // Preserve scroll position
      const newCursorPos = cursorPos + `{{amazon:${productId}}}\n\n`.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  /** Wrap current selection (or empty block at cursor) in {{affiliate}}…{{/affiliate}}. */
  const wrapSelectionInAffiliateBlock = () => {
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = value.substring(start, end);
    const wrapped = wrapInAffiliateBlock(selected);
    const newText = value.substring(0, start) + wrapped + value.substring(end);
    onChange(newText);
    setTimeout(() => {
      textarea.focus();
      const cursor = start + wrapped.length;
      textarea.setSelectionRange(cursor, cursor);
    }, 0);
  };

  const insertExperience = (experienceId: string) => {
    // Get current cursor position
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const textBefore = value.substring(0, cursorPos);
    const textAfter = value.substring(cursorPos);
    const scrollTop = textarea.scrollTop; // Preserve scroll position
    
    // Insert experience shortcode at cursor position (using {{}} format like other embeds)
    const newText = textBefore + `{{experience:${experienceId}}}\n\n` + textAfter;
    onChange(newText);
    
    // Set cursor position after the inserted experience
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop; // Restore scroll position
      const newCursorPos = cursorPos + `{{experience:${experienceId}}}\n\n`.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const getBlockColors = (blockType: string) => {
    switch (blockType) {
      case 'extracted':
        return {
          bg: 'bg-green-500',
          border: 'border-green-500',
          text: 'text-green-600',
          bgLight: 'bg-green-50',
          borderLight: 'border-green-200'
        };
      case 'estimation':
        return {
          bg: 'bg-yellow-500',
          border: 'border-yellow-500',
          text: 'text-yellow-600',
          bgLight: 'bg-yellow-50',
          borderLight: 'border-yellow-200'
        };
      case 'calculated':
        return {
          bg: 'bg-blue-500',
          border: 'border-blue-500',
          text: 'text-blue-600',
          bgLight: 'bg-blue-50',
          borderLight: 'border-blue-200'
        };
      case 'assumption':
        return {
          bg: 'bg-gray-500',
          border: 'border-gray-500',
          text: 'text-gray-600',
          bgLight: 'bg-gray-50',
          borderLight: 'border-gray-200'
        };
      default:
        return {
          bg: 'bg-gray-500',
          border: 'border-gray-500',
          text: 'text-gray-600',
          bgLight: 'bg-gray-50',
          borderLight: 'border-gray-200'
        };
    }
  };

  const processFacts = (html: string): string => {
    return html.replace(/\{\{fact:([^}]+)\}\}/g, (match, factId) => {
      const fact = facts.find(f => f.id === factId);
      if (!fact) {
        return `<div class="bg-red-50 border border-red-200 rounded-lg p-4 my-4 text-center"><div class="text-red-600 font-medium">❌ Fact no encontrado: ${factId}</div></div>`;
      }

      const colors = getBlockColors(fact.type);
      const blockNumber = fact.id.replace('P', '');
      
      // Get operation symbol for display
      const getOperationSymbol = (unit: string): string => {
        switch (unit) {
          case 'PERCENTAGE': return '%';
          case 'ADDITION': return '+';
          case 'SUBTRACTION': return '-';
          case 'MULTIPLICATION': return '×';
          case 'DIVISION': return '÷';
          default: return unit;
        }
      };

      // Function to format unit display
      const formatUnitDisplay = (block: StatisticBlock): string => {
        if (!block.unit) return '';
        
        if (['PERCENTAGE', 'ADDITION', 'SUBTRACTION', 'MULTIPLICATION', 'DIVISION'].includes(block.unit)) {
          return `<span class="text-2xl font-light"> ${getOperationSymbol(block.unit)}</span>`;
        }
        
        return `<span class="text-2xl font-light">${block.unit}</span>`;
      };
      
      return `
        <div class="my-8">
          <!-- Fact Container - No badge, aligned to left -->
          <div class="${colors.bgLight} border-l-4 ${colors.border} p-4 rounded-r-lg relative">
            <!-- Title at the top -->
            <h4 class="text-xl font-semibold text-foreground mb-2 font-serif">
              ${fact.title}
            </h4>
            
            ${fact.source ? `
              <!-- Info Icon for Source -->
              <div class="absolute top-2 right-2">
                <div class="relative">
                  <button 
                    class="w-6 h-6 bg-white/80 rounded-full flex items-center justify-center cursor-help hover:bg-white transition-colors"
                    onclick="
                      const tooltip = this.nextElementSibling;
                      const isVisible = !tooltip.classList.contains('opacity-0');
                      if (isVisible) {
                        tooltip.classList.add('opacity-0');
                        tooltip.classList.remove('opacity-100');
                      } else {
                        tooltip.classList.remove('opacity-0');
                        tooltip.classList.add('opacity-100');
                        // Close other tooltips
                        document.querySelectorAll('.tooltip-source').forEach(t => {
                          if (t !== tooltip) {
                            t.classList.add('opacity-0');
                            t.classList.remove('opacity-100');
                          }
                        });
                      }
                    "
                  >
                    <svg class="w-4 h-4 text-muted-foreground" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                    </svg>
                  </button>
                  <!-- Tooltip -->
                  <div class="tooltip-source absolute bottom-full right-0 mb-2 w-64 bg-black text-white text-sm rounded-lg p-3 opacity-0 transition-opacity z-20">
                    <div class="font-medium mb-1">Fuente:</div>
                    <div class="text-xs leading-relaxed">
                      ${fact.source}
                      ${fact.source_url ? `
                        <div class="mt-2">
                          <a href="${fact.source_url}" target="_blank" rel="noopener noreferrer" class="text-blue-300 hover:text-blue-200 underline">
                            Ver fuente original →
                          </a>
                        </div>
                      ` : ''}
                    </div>
                    <!-- Arrow -->
                    <div class="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
                  </div>
                </div>
              </div>
            ` : ''}
            
            <!-- Value with icon -->
            <div class="flex items-center gap-3 mb-2">
              ${fact.image_icon ? (isSvgContent(fact.image_icon) ? 
                `<div class="w-10 h-10 flex items-center justify-center overflow-hidden" data-svg-content="${encodeURIComponent(extractSvgContent(fact.image_icon))}"></div>` :
                `<img src="${fact.image_icon}" alt="Icono ${fact.title}" class="w-10 h-10 object-contain" />`) : ''}
              <div class="text-4xl font-bold ${colors.text}">
                ${fact.value !== undefined && fact.value !== null 
                  ? (fact.value === 0 ? '0' : fact.value.toLocaleString())
                  : 'N/A'} <span>${formatUnitDisplay(fact)}</span>
              </div>
            </div>
            
            <!-- Description inside the container -->
            <p class="text-base text-foreground leading-snug mb-2 font-serif whitespace-pre-line">
              ${fact.description}
            </p>
            
            <!-- Type label at the bottom -->
            <div class="text-sm text-muted-foreground uppercase tracking-wide">
              ${fact.type === 'extracted' && 'Dato extraído'}
              ${fact.type === 'estimation' && 'Estimación'}
              ${fact.type === 'calculated' && 'Cálculo'}
              ${fact.type === 'assumption' && 'Suposición'}
            </div>
          </div>
        </div>
      `;
    });
  };

  const insertUnorderedList = () => {
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const scrollTop = textarea.scrollTop; // Preserve scroll position
    
    let newText: string;
    let newCursorStart: number;
    let newCursorEnd: number;
    
    if (selectedText) {
      const lines = selectedText.split('\n');
      
      // Check if all lines are already unordered list items
      const allLinesAreUnordered = lines.every(line => {
        const trimmed = line.trim();
        return trimmed === '' || trimmed.startsWith('- ');
      });
      
      if (allLinesAreUnordered) {
        // Remove list formatting (toggle off)
        const unformattedItems = lines.map(line => {
          const trimmed = line.trim();
          return trimmed.startsWith('- ') ? trimmed.substring(2) : trimmed;
        }).join('\n');
        
        newText = value.substring(0, start) + unformattedItems + value.substring(end);
        newCursorStart = start;
        newCursorEnd = start + unformattedItems.length;
      } else {
        // Add list formatting (toggle on)
        const listItems = lines.map(line => line.trim() ? `- ${line.trim()}` : '- ').join('\n');
        
        newText = value.substring(0, start) + listItems + value.substring(end);
        newCursorStart = start;
        newCursorEnd = start + listItems.length;
      }
    } else {
      // If no text selected, insert single list item
      newText = value.substring(0, start) + '- ' + value.substring(end);
      newCursorStart = start + 2; // After "- "
      newCursorEnd = newCursorStart;
    }
    
    onChange(newText);
    
    // Set cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop; // Restore scroll position
      textarea.setSelectionRange(newCursorStart, newCursorEnd);
    }, 0);
  };

  const insertOrderedList = () => {
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const scrollTop = textarea.scrollTop; // Preserve scroll position
    
    let newText: string;
    let newCursorStart: number;
    let newCursorEnd: number;
    
    if (selectedText) {
      const lines = selectedText.split('\n');
      
      // Check if all lines are already ordered list items
      const allLinesAreOrdered = lines.every(line => {
        const trimmed = line.trim();
        return trimmed === '' || /^\d+\. /.test(trimmed);
      });
      
      if (allLinesAreOrdered) {
        // Remove list formatting (toggle off)
        const unformattedItems = lines.map(line => {
          const trimmed = line.trim();
          return /^\d+\. /.test(trimmed) ? trimmed.replace(/^\d+\. /, '') : trimmed;
        }).join('\n');
        
        newText = value.substring(0, start) + unformattedItems + value.substring(end);
        newCursorStart = start;
        newCursorEnd = start + unformattedItems.length;
      } else {
        // Add list formatting (toggle on)
        const listItems = lines.map((line, index) => {
          const trimmedLine = line.trim();
          return trimmedLine ? `${index + 1}. ${trimmedLine}` : `${index + 1}. `;
        }).join('\n');
        
        newText = value.substring(0, start) + listItems + value.substring(end);
        newCursorStart = start;
        newCursorEnd = start + listItems.length;
      }
    } else {
      // If no text selected, insert single list item
      newText = value.substring(0, start) + '1. ' + value.substring(end);
      newCursorStart = start + 3; // After "1. "
      newCursorEnd = newCursorStart;
    }
    
    onChange(newText);
    
    // Set cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop; // Restore scroll position
      textarea.setSelectionRange(newCursorStart, newCursorEnd);
    }, 0);
  };

  const insertTable = () => {
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const scrollTop = textarea.scrollTop; // Preserve scroll position
    
    // Create a basic 3x3 table
    const tableMarkdown = `| Columna 1 | Columna 2 | Columna 3 |
|-----------|-----------|-----------|
| Celda 1   | Celda 2   | Celda 3   |
| Celda 4   | Celda 5   | Celda 6   |`;
    
    const newText = value.substring(0, start) + tableMarkdown + value.substring(end);
    onChange(newText);
    
    // Set cursor position after the table
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop; // Restore scroll position
      const newCursorPos = start + tableMarkdown.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  /** Insert a LaTeX math snippet; wraps selection into the first slot when useful */
  const insertMathSnippet = (snippet: MathSnippet) => {
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end).trim();
    const scrollTop = textarea.scrollTop;

    let body = snippet.body;
    if (selectedText) {
      if (snippet.selectToken?.startsWith('{') && snippet.selectToken.endsWith('}')) {
        body = body.replace(snippet.selectToken, `{${selectedText}}`);
      } else if (snippet.selectToken && body.includes(snippet.selectToken)) {
        body = body.replace(snippet.selectToken, selectedText);
      } else if (body.includes('{a}')) {
        body = body.replace('{a}', `{${selectedText}}`);
      } else {
        body = selectedText;
      }
    }

    let wrapped: string;
    let bodyOffset: number;
    if (snippet.displayMode) {
      wrapped = `\n$$\n${body}\n$$\n`;
      bodyOffset = start + 4; // after \n$$\n
    } else {
      wrapped = `$${body}$`;
      bodyOffset = start + 1;
    }

    // Highlight the editable slot (inner content if token is {…})
    let highlight = selectedText || body;
    let highlightIndex = 0;
    if (!selectedText && snippet.selectToken && body.includes(snippet.selectToken)) {
      const tokenIndex = body.indexOf(snippet.selectToken);
      if (snippet.selectToken.startsWith('{') && snippet.selectToken.endsWith('}')) {
        highlight = snippet.selectToken.slice(1, -1);
        highlightIndex = tokenIndex + 1;
      } else {
        highlight = snippet.selectToken;
        highlightIndex = tokenIndex;
      }
    } else if (selectedText) {
      highlightIndex = body.indexOf(selectedText);
      if (highlightIndex < 0) highlightIndex = 0;
    }

    const selectStart = bodyOffset + highlightIndex;
    const selectEnd = selectStart + highlight.length;

    const newText = value.substring(0, start) + wrapped + value.substring(end);
    onChange(newText);
    setShowMathMenu(false);

    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop;
      textarea.setSelectionRange(selectStart, selectEnd);
    }, 0);
  };

  const insertImage = () => {
    setShowImageOptions(true);
  };

  const handleUploadImage = (imageData: {
    filename: string;
    mimeType: string;
    size: number;
    data: string;
    source: 'upload';
    metadata: any;
  }) => {
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const scrollTop = textarea.scrollTop;
    
    // Create temporary URL for uploaded image
    const tempUrl = `data:${imageData.mimeType};base64,${imageData.data}`;
    
    // Create HTML for direct image insertion
    const imageHtml = `<div class="my-4">
  <img src="${tempUrl}" alt="Texto alternativo" class="max-w-full h-auto rounded-lg border border-surface-200" />
</div>`;
    
    const newText = value.substring(0, start) + imageHtml + value.substring(end);
    onChange(newText);
    
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop;
      const newPosition = start + imageHtml.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
    
    setShowUploadModal(false);
  };

  const handleStockImageSelect = (stockImage: any) => {
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const scrollTop = textarea.scrollTop;
    
    // Create HTML for direct image insertion with attribution
    const imageHtml = `<div class="my-4">
  <img src="${stockImage.url}" alt="Texto alternativo" class="max-w-full h-auto rounded-lg border border-surface-200" />
  <div class="mt-2 text-sm text-muted-foreground italic text-center">
    <em>Fuente: <a href="${stockImage.source === 'pexels' ? 'https://www.pexels.com/' : 'https://www.freepik.es/'}" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">${stockImage.source === 'pexels' ? 'Pexels' : 'Freepik'}</a></em>
  </div>
</div>`;
    
    const newText = value.substring(0, start) + imageHtml + value.substring(end);
    onChange(newText);
    
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop;
      const newPosition = start + imageHtml.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
    
    setShowStockModal(false);
  };

  const handleAIImageGenerate = (imageData: {
    filename: string;
    mimeType: string;
    size: number;
    data: string;
    source: 'ai';
    metadata: any;
  }) => {
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const scrollTop = textarea.scrollTop;
    
    // Create temporary URL for AI generated image
    const tempUrl = `data:${imageData.mimeType};base64,${imageData.data}`;
    
    // Create HTML for direct image insertion with AI attribution
    const imageHtml = `<div class="my-4">
  <img src="${tempUrl}" alt="Texto alternativo" class="max-w-full h-auto rounded-lg border border-surface-200" />
  <div class="mt-2 text-sm text-muted-foreground italic text-center">
    <em>Imagen generada con IA</em>
  </div>
</div>`;
    
    const newText = value.substring(0, start) + imageHtml + value.substring(end);
    onChange(newText);
    
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop;
      const newPosition = start + imageHtml.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
    
    setShowAIModal(false);
  };

  const insertVideo = () => {
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const scrollTop = textarea.scrollTop; // Preserve scroll position
    
    // Create video placeholder
    const videoMarkdown = `{{video:URL_del_video}}`;
    
    const newText = value.substring(0, start) + videoMarkdown + value.substring(end);
    onChange(newText);
    
    // Set cursor position to select the URL
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop; // Restore scroll position
      const urlStart = start + videoMarkdown.indexOf(':') + 1;
      const urlEnd = start + videoMarkdown.indexOf('}');
      textarea.setSelectionRange(urlStart, urlEnd);
    }, 0);
  };

  const insertAudio = () => {
    const textarea = document.getElementById('markdown-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const scrollTop = textarea.scrollTop; // Preserve scroll position
    
    // Create audio placeholder
    const audioMarkdown = `{{audio:URL_del_audio}}`;
    
    const newText = value.substring(0, start) + audioMarkdown + value.substring(end);
    onChange(newText);
    
    // Set cursor position to select the URL
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop; // Restore scroll position
      const urlStart = start + audioMarkdown.indexOf(':') + 1;
      const urlEnd = start + audioMarkdown.indexOf('}');
      textarea.setSelectionRange(urlStart, urlEnd);
    }, 0);
  };

  const renderMarkdown = (text: string) => {
    return withMathMarkdown(text, (safeText) => {
      // Basic Markdown parsing (math already extracted as placeholders)
      let html = safeText
        // Headers (must be processed first, before line breaks)
        .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold text-foreground">$1</h3>')
        .replace(/^## (.*$)/gim, '<h2 class="text-2xl font-semibold text-foreground">$1</h2>')
        .replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold text-foreground">$1</h1>')
        // Bold
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        // Italic
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        // Links
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$1</a>')
        // Images with attribution (markdown format - legacy support)
        .replace(/!\[([^\]]*)\]\(([^)]+)\)\s*\n\s*\*Fuente: \[([^\]]+)\]\(([^)]+)\)\*/g, '<div class="my-4"><img src="$2" alt="$1" class="max-w-full h-auto rounded-lg border border-surface-200" /><div class="mt-2 text-sm text-muted-foreground italic text-center"><em>Fuente: <a href="$4" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$3</a></em></div></div>')
        // Images with simple attribution (markdown format - legacy support)
        .replace(/!\[([^\]]*)\]\(([^)]+)\)\s*\n\s*\*Fuente: ([^*]+)\*/g, '<div class="my-4"><img src="$2" alt="$1" class="max-w-full h-auto rounded-lg border border-surface-200" /><div class="mt-2 text-sm text-muted-foreground italic text-center"><em>Fuente: $3</em></div></div>')
        // Images with AI attribution (markdown format - legacy support)
        .replace(/!\[([^\]]*)\]\(([^)]+)\)\s*\n\s*\*Imagen generada con IA\*/g, '<div class="my-4"><img src="$2" alt="$1" class="max-w-full h-auto rounded-lg border border-surface-200" /><div class="mt-2 text-sm text-muted-foreground italic text-center"><em>Imagen generada con IA</em></div></div>')
        // Regular images without attribution (markdown format - legacy support)
        .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<div class="my-4"><img src="$2" alt="$1" class="max-w-full h-auto rounded-lg border border-surface-200" /></div>')
        // Chart placeholders
        .replace(/\{\{chart:([^}]+)\}\}/g, '<div class="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4 text-center"><div class="text-blue-600 font-medium">📊 Gráfico: $1</div><div class="text-sm text-blue-500">(Se renderizará el gráfico con ID: $1)</div></div>')
        // Highlight placeholders
        .replace(/\{\{highlight:([^}]+)\}\}/g, '<div class="bg-orange-50 border border-orange-200 rounded-lg p-4 my-4 text-center"><div class="text-orange-600 font-medium">💡 Highlight: $1</div><div class="text-sm text-orange-500">(Se renderizará el highlight con ID: $1)</div></div>')
        // Segment placeholders
        .replace(/\{\{segment:([^}]+)\}\}/g, '<div class="bg-green-50 border border-green-200 rounded-lg p-4 my-4 text-center"><div class="text-green-600 font-medium">📊 Segmentación: $1</div><div class="text-sm text-green-500">(Se renderizará la segmentación con dimensión: $1)</div></div>')
        // Video placeholders
        .replace(/\{\{video:([^}]+)\}\}/g, '<div class="bg-purple-50 border border-purple-200 rounded-lg p-4 my-4 text-center"><div class="text-purple-600 font-medium">🎥 Video: $1</div><div class="text-sm text-purple-500">(Se renderizará el video con URL: $1)</div></div>')
        // Audio placeholders
        .replace(/\{\{audio:([^}]+)\}\}/g, '<div class="bg-indigo-50 border border-indigo-200 rounded-lg p-4 my-4 text-center"><div class="text-indigo-600 font-medium">🎵 Audio: $1</div><div class="text-sm text-indigo-500">(Se renderizará el audio con URL: $1)</div></div>')
        // Experience placeholders
        .replace(/\{\{experience:([^}]+)\}\}/g, '<div class="bg-purple-50 border border-purple-200 rounded-lg p-4 my-4 text-center"><div class="text-purple-600 font-medium">🎮 Experiencia Interactiva: $1</div><div class="text-sm text-purple-500">(Se renderizará el iframe con la experiencia)</div></div>')
        // Line breaks
        .replace(/\n/g, '<br>');

      // Process facts after basic markdown processing
      html = processFacts(html);

      // Process lists (ordered and unordered)
      html = processLists(html);

      // Process tables
      html = processTables(html);

      return html;
    });
  };

  const processLists = (html: string): string => {
    // Split by line breaks to process line by line
    const lines = html.split('<br>');
    const processedLines: string[] = [];
    let inUnorderedList = false;
    let inOrderedList = false;
    let listItems: string[] = [];
    let lastWasEmpty = false;

    const flushList = () => {
      if (listItems.length > 0) {
        if (inUnorderedList) {
          processedLines.push(`<ul class="list-disc list-outside space-y-1 ml-6 pl-2">${listItems.join('')}</ul>`);
        } else if (inOrderedList) {
          processedLines.push(`<ol class="list-decimal list-outside space-y-1 ml-6 pl-2">${listItems.join('')}</ol>`);
        }
        listItems = [];
        lastWasEmpty = false; // Reset empty line tracking after list
      }
      inUnorderedList = false;
      inOrderedList = false;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();
      
      // Check for unordered list item
      if (trimmedLine.startsWith('- ')) {
        if (!inUnorderedList) {
          flushList();
          inUnorderedList = true;
        }
        const content = trimmedLine.substring(2);
        listItems.push(`<li class="text-foreground">${content}</li>`);
        lastWasEmpty = false;
      }
      // Check for ordered list item
      else if (/^\d+\. /.test(trimmedLine)) {
        if (!inOrderedList) {
          flushList();
          inOrderedList = true;
        }
        const content = trimmedLine.replace(/^\d+\. /, '');
        listItems.push(`<li class="text-foreground">${content}</li>`);
        lastWasEmpty = false;
      }
      // Empty line
      else if (trimmedLine === '') {
        if (inUnorderedList || inOrderedList) {
          // Don't add empty lines inside lists
          continue;
        } else {
          // Only add empty line if previous line wasn't empty
          if (!lastWasEmpty) {
            processedLines.push('');
            lastWasEmpty = true;
          }
        }
      }
      // Regular line
      else {
        flushList();
        processedLines.push(line);
        lastWasEmpty = false;
      }
    }

    // Flush any remaining list
    flushList();

    // Join lines, but avoid adding <br> after list elements
    return processedLines.join('<br>');
  };

  const processTables = (html: string): string => {
    // Split by line breaks to process line by line
    const lines = html.split('<br>');
    const processedLines: string[] = [];
    let tableLines: string[] = [];
    let inTable = false;

    const flushTable = () => {
      if (tableLines.length > 0) {
        const tableHtml = convertTableToHtml(tableLines);
        processedLines.push(tableHtml);
        tableLines = [];
      }
      inTable = false;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();
      
      // Check if line is a table row (starts with | and ends with |)
      if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
        if (!inTable) {
          flushTable();
          inTable = true;
        }
        tableLines.push(trimmedLine);
      } else {
        flushTable();
        processedLines.push(line);
      }
    }

    // Flush any remaining table
    flushTable();

    return processedLines.join('<br>');
  };

  const convertTableToHtml = (tableLines: string[]): string => {
    if (tableLines.length < 2) return tableLines.join('<br>');

    const rows = tableLines.map(line => {
      // Remove leading and trailing |
      const cells = line.slice(1, -1).split('|').map(cell => cell.trim());
      return cells;
    });

    // First row is header, second row is separator (ignore it)
    const headerRow = rows[0];
    const dataRows = rows.slice(2); // Skip separator row

    let tableHtml = '<div class="overflow-x-auto my-4"><table class="min-w-full border border-surface-200 rounded-lg">';
    
    // Header
    tableHtml += '<thead class="bg-surface-50">';
    tableHtml += '<tr>';
    headerRow.forEach(cell => {
      tableHtml += `<th class="border border-surface-200 px-4 py-2 text-left font-semibold text-foreground">${cell}</th>`;
    });
    tableHtml += '</tr>';
    tableHtml += '</thead>';
    
    // Body
    tableHtml += '<tbody>';
    dataRows.forEach(row => {
      tableHtml += '<tr>';
      row.forEach(cell => {
        tableHtml += `<td class="border border-surface-200 px-4 py-2 text-foreground">${cell}</td>`;
      });
      tableHtml += '</tr>';
    });
    tableHtml += '</tbody>';
    
    tableHtml += '</table></div>';
    
    return tableHtml;
  };

  return (
    <div className={`border border-border overflow-hidden ${fullscreen ? 'h-full flex flex-col' : 'rounded-lg'} ${className}`}>
      {!areAmazonAffiliatesEnabled() && (
        <div className="px-3 py-2 text-xs bg-amber-50 text-amber-900 border-b border-amber-200">
          Afiliados Amazon ocultos en la web pública (kill-switch). El contenido se guarda igual; envuelve el pitch con el botón «Bloque afiliado».
        </div>
      )}
      {/* Toolbar */}
      <div className={`flex items-center justify-between bg-surface-50 border-b border-border ${fullscreen ? 'p-4' : 'p-3'}`}>
        <div className="flex items-center gap-1">
          {/* Header buttons - First */}
          <div className="flex items-center gap-1 border-r border-border pr-2">
            <button
              type="button"
              onClick={() => insertHeader(1)}
              className="px-2 py-1 hover:bg-surface-200 rounded transition-colors text-sm font-medium"
              title="Título Principal (H1)"
            >
              H1
            </button>
            <button
              type="button"
              onClick={() => insertHeader(2)}
              className="px-2 py-1 hover:bg-surface-200 rounded transition-colors text-sm font-medium"
              title="Subtítulo (H2)"
            >
              H2
            </button>
            <button
              type="button"
              onClick={() => insertHeader(3)}
              className="px-2 py-1 hover:bg-surface-200 rounded transition-colors text-sm font-medium"
              title="Sub-subtítulo (H3)"
            >
              H3
            </button>
          </div>

          {/* AI text assistant */}
          <div className="flex items-center border-r border-border pr-2 mr-1">
            <button
              type="button"
              onClick={openAITextEditor}
              disabled={isPreview}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600 text-white text-sm font-medium hover:from-violet-600 hover:to-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm shadow-violet-500/25"
              title="Editar o ampliar texto con IA (usa selección o cursor)"
            >
              <Sparkles size={14} />
              IA
            </button>
          </div>
          
          <button
            type="button"
            onClick={() => insertText('**', '**')}
            className="p-2 hover:bg-surface-200 rounded transition-colors"
            title="Negrita"
          >
            <Bold size={16} />
          </button>
          <button
            type="button"
            onClick={() => insertText('*', '*')}
            className="p-2 hover:bg-surface-200 rounded transition-colors"
            title="Cursiva"
          >
            <Italic size={16} />
          </button>
          <button
            type="button"
            onClick={() => insertText('[', '](url)')}
            className="p-2 hover:bg-surface-200 rounded transition-colors"
            title="Enlace"
          >
            <Link size={16} />
          </button>
          
          {/* List buttons */}
          <button
            type="button"
            onClick={insertUnorderedList}
            className="p-2 hover:bg-surface-200 rounded transition-colors"
            title="Lista no ordenada"
          >
            <List size={16} />
          </button>
          <button
            type="button"
            onClick={insertOrderedList}
            className="p-2 hover:bg-surface-200 rounded transition-colors"
            title="Lista ordenada"
          >
            <ListOrdered size={16} />
          </button>
          
          {/* Table button */}
          <button
            type="button"
            onClick={insertTable}
            className="p-2 hover:bg-surface-200 rounded transition-colors"
            title="Tabla"
          >
            <Table size={16} />
          </button>

          {/* Math formulas palette */}
          <div className="relative math-menu-container">
            <button
              type="button"
              onClick={() => setShowMathMenu(!showMathMenu)}
              disabled={isPreview}
              className="p-2 hover:bg-surface-200 rounded transition-colors flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Insertar fórmula matemática (LaTeX)"
            >
              <Sigma size={16} />
              <Plus size={12} />
            </button>
            {showMathMenu && (
              <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-lg shadow-lg z-20 w-72 max-h-80 overflow-hidden">
                <div className="p-2 text-xs text-muted-foreground border-b border-border">
                  Insertar fórmula (LaTeX)
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {MATH_SNIPPETS.map((snippet) => (
                    <button
                      key={snippet.id}
                      type="button"
                      onClick={() => insertMathSnippet(snippet)}
                      className="w-full text-left px-3 py-2 hover:bg-surface-100 text-sm border-b border-border last:border-b-0 flex items-center gap-3"
                    >
                      <span className="w-10 shrink-0 text-center font-serif text-base text-primary tabular-nums">
                        {snippet.preview}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium text-foreground">{snippet.label}</span>
                        <span className="block text-xs text-muted-foreground font-mono truncate">
                          {snippet.hint}
                        </span>
                      </span>
                      {snippet.displayMode && (
                        <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">
                          bloque
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Multimedia buttons */}
          <button
            type="button"
            onClick={insertImage}
            className="p-2 hover:bg-surface-200 rounded transition-colors"
            title="Imagen"
          >
            <FileImage size={16} />
          </button>
          <button
            type="button"
            onClick={insertVideo}
            className="p-2 hover:bg-surface-200 rounded transition-colors"
            title="Video"
          >
            <Video size={16} />
          </button>
          <button
            type="button"
            onClick={insertAudio}
            className="p-2 hover:bg-surface-200 rounded transition-colors"
            title="Audio"
          >
            <Music size={16} />
          </button>
          
          <button
            type="button"
            onClick={insertLineBreak}
            className="p-2 hover:bg-surface-200 rounded transition-colors"
            title="Salto de línea"
          >
            <CornerDownLeft size={16} />
          </button>
          
          {/* Chart selector */}
          {charts.length > 0 && (
            <div className="relative chart-menu-container">
              <button
                type="button"
                onClick={() => setShowChartMenu(!showChartMenu)}
                className="p-2 hover:bg-surface-200 rounded transition-colors flex items-center gap-1"
                title="Insertar gráfico"
              >
                <Image size={16} />
                <Plus size={12} />
              </button>
              {showChartMenu && (
                <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-lg shadow-lg z-10 min-w-48">
                  <div className="p-2 text-xs text-muted-foreground border-b border-border">
                    Seleccionar gráfico:
                  </div>
                  {charts.map((chart) => (
                    <button
                      key={chart.id}
                      type="button"
                      onClick={() => {
                        insertChart(chart.id);
                        setShowChartMenu(false);
                      }}
                      className="w-full text-left p-2 hover:bg-surface-100 text-sm"
                    >
                      {chart.title || `Gráfico ${chart.id}`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Highlight selector */}
          {highlights.length > 0 && (
            <div className="relative highlight-menu-container">
              <button
                type="button"
                onClick={() => setShowHighlightMenu(!showHighlightMenu)}
                className="p-2 hover:bg-surface-200 rounded transition-colors flex items-center gap-1"
                title="Insertar highlight"
              >
                <span className="text-lg">💡</span>
                <Plus size={12} />
              </button>
              {showHighlightMenu && (
                <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-lg shadow-lg z-10 min-w-48">
                  <div className="p-2 text-xs text-muted-foreground border-b border-border">
                    Seleccionar highlight:
                  </div>
                  {highlights.map((highlight) => (
                    <button
                      key={highlight.id}
                      type="button"
                      onClick={() => {
                        insertHighlight(highlight.id);
                        setShowHighlightMenu(false);
                      }}
                      className="w-full text-left p-2 hover:bg-surface-100 text-sm"
                    >
                      {highlight.text}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Segment selector */}
          {segments.length > 0 && (
            <div className="relative segment-menu-container">
              <button
                type="button"
                onClick={() => setShowSegmentMenu(!showSegmentMenu)}
                className="p-2 hover:bg-surface-200 rounded transition-colors flex items-center gap-1"
                title="Insertar segmentación"
              >
                <span className="text-lg">📊</span>
                <Plus size={12} />
              </button>
              {showSegmentMenu && (
                <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-lg shadow-lg z-10 min-w-48">
                  <div className="p-2 text-xs text-muted-foreground border-b border-border">
                    Seleccionar segmentación:
                  </div>
                  {segments.map((segment) => (
                    <button
                      key={segment.dimension}
                      type="button"
                      onClick={() => {
                        insertSegment(segment.dimension);
                        setShowSegmentMenu(false);
                      }}
                      className="w-full text-left p-2 hover:bg-surface-100 text-sm"
                    >
                      {formatSegmentLabel(segment)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Fact selector */}
          {facts.length > 0 && (
            <div className="relative fact-menu-container">
              <button
                type="button"
                onClick={() => setShowFactMenu(!showFactMenu)}
                className="p-2 hover:bg-surface-200 rounded transition-colors flex items-center gap-1"
                title="Insertar fact"
              >
                <span className="text-lg">📋</span>
                <Plus size={12} />
              </button>
              {showFactMenu && (
                <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-lg shadow-lg z-10 min-w-64 max-h-80 overflow-hidden">
                  <div className="p-2 text-xs text-muted-foreground border-b border-border">
                    Seleccionar fact:
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {facts.map((fact) => {
                      const colors = getBlockColors(fact.type);
                      return (
                        <button
                          key={fact.id}
                          type="button"
                          onClick={() => {
                            insertFact(fact.id);
                            setShowFactMenu(false);
                          }}
                          className="w-full text-left p-3 hover:bg-surface-100 text-sm border-b border-border last:border-b-0"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <div className={`w-4 h-4 ${colors.bg} rounded-full flex items-center justify-center text-xs text-white font-bold`}>
                              {fact.id.replace('P', '')}
                            </div>
                            <span className="font-medium">{fact.title}</span>
                            {fact.hidden && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">sin análisis</span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {fact.value?.toLocaleString()} {fact.unit || ''}
                          </div>
                          <div className="text-xs text-muted-foreground capitalize">
                            {fact.type}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Amazon Product selector */}
          {amazonProducts.length > 0 && (
            <div className="relative amazon-menu-container">
              <button
                type="button"
                onClick={() => setShowAmazonProductMenu(!showAmazonProductMenu)}
                className="p-2 hover:bg-surface-200 rounded transition-colors flex items-center gap-1"
                title="Insertar producto Amazon"
              >
                <ShoppingCart size={16} />
                <Plus size={12} />
              </button>
              {showAmazonProductMenu && (
                <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-lg shadow-lg z-10 min-w-64 max-h-80 overflow-hidden">
                  <div className="p-2 text-xs text-muted-foreground border-b border-border">
                    Seleccionar producto Amazon:
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {amazonProducts.map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => {
                          insertAmazonProduct(product.id);
                          setShowAmazonProductMenu(false);
                        }}
                        className="w-full text-left p-3 hover:bg-surface-100 text-sm border-b border-border last:border-b-0"
                      >
                        <div className="flex items-center space-x-2">
                          {product.imageUrl && (
                            <img
                              src={product.imageUrl}
                              alt={product.title}
                              className="w-8 h-8 object-cover rounded"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{product.title}</div>
                            {product.price && (
                              <div className="text-xs text-muted-foreground">{product.price}</div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={wrapSelectionInAffiliateBlock}
            disabled={isPreview}
            className="p-2 hover:bg-surface-200 rounded transition-colors flex items-center gap-1 disabled:opacity-40"
            title="Envolver selección en bloque afiliado {{affiliate}}…{{/affiliate}}"
          >
            <SquareStack size={16} />
            <span className="text-xs hidden sm:inline">Bloque afiliado</span>
          </button>

          {/* Experience selector */}
          {experiences.length > 0 && (
            <div className="relative experience-menu-container">
              <button
                type="button"
                onClick={() => setShowExperienceMenu(!showExperienceMenu)}
                className="p-2 hover:bg-surface-200 rounded transition-colors flex items-center gap-1"
                title="Insertar experiencia interactiva"
              >
                <Gamepad2 size={16} />
                <Plus size={12} />
              </button>
              {showExperienceMenu && (
                <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-lg shadow-lg z-10 min-w-64 max-h-80 overflow-hidden">
                  <div className="p-2 text-xs text-muted-foreground border-b border-border">
                    Seleccionar experiencia interactiva:
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {experiences.map((experience) => (
                      <button
                        key={experience.id}
                        type="button"
                        onClick={() => {
                          insertExperience(experience.id);
                          setShowExperienceMenu(false);
                        }}
                        className="w-full text-left p-3 hover:bg-surface-100 text-sm border-b border-border last:border-b-0"
                      >
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center text-blue-600">
                            <Gamepad2 size={16} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{experience.title}</div>
                            {experience.description && (
                              <div className="text-xs text-muted-foreground truncate">{experience.description}</div>
                            )}
                            <div className="text-xs text-blue-500 capitalize">{experience.type}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        <button
          type="button"
          onClick={() => setIsPreview(!isPreview)}
          className="p-2 hover:bg-surface-200 rounded transition-colors flex items-center gap-1"
          title={isPreview ? "Editar" : "Vista previa"}
        >
          {isPreview ? <Edit size={16} /> : <Eye size={16} />}
          <span className="text-sm">{isPreview ? "Editar" : "Vista previa"}</span>
        </button>
      </div>

      {/* Editor/Preview */}
      {isPreview ? (
        <div className={`prose prose-sm max-w-none overflow-y-auto ${fullscreen ? 'p-6 flex-1' : 'p-4 min-h-[200px]'}`}>
          <div 
            dangerouslySetInnerHTML={{ __html: renderMarkdown(value) }}
            className="whitespace-pre-wrap"
          />
        </div>
      ) : (
        <textarea
          id="markdown-editor"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full border-none focus:outline-none focus:ring-0 resize-none ${fullscreen ? 'p-6 flex-1 text-base' : 'p-4 min-h-[200px] resize-y'}`}
        />
        )}

        {/* Image Options Modal */}
        {showImageOptions && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background border border-border rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center justify-between p-6 border-b border-border">
                <h3 className="text-lg font-semibold text-foreground">Seleccionar Imagen</h3>
                <button
                  onClick={() => setShowImageOptions(false)}
                  className="p-2 hover:bg-surface-200 rounded transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <button
                  onClick={() => {
                    setShowImageOptions(false);
                    setShowUploadModal(true);
                  }}
                  className="w-full flex items-center gap-3 p-4 border border-border rounded-lg hover:bg-surface-100 transition-colors text-left"
                >
                  <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                    <Upload size={20} />
                  </div>
                  <div>
                    <div className="font-medium">Subir archivo</div>
                    <div className="text-sm text-muted-foreground">Seleccionar imagen desde tu dispositivo</div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setShowImageOptions(false);
                    setShowStockModal(true);
                  }}
                  className="w-full flex items-center gap-3 p-4 border border-border rounded-lg hover:bg-surface-100 transition-colors text-left"
                >
                  <div className="p-2 bg-green-100 text-green-600 rounded-lg">
                    <Search size={20} />
                  </div>
                  <div>
                    <div className="font-medium">Buscar en stock gratuito</div>
                    <div className="text-sm text-muted-foreground">Encontrar imágenes libres de derechos</div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setShowImageOptions(false);
                    setShowAIModal(true);
                  }}
                  className="w-full flex items-center gap-3 p-4 border border-border rounded-lg hover:bg-surface-100 transition-colors text-left"
                >
                  <div className="p-2 bg-purple-100 text-purple-600 rounded-lg">
                    <Sparkles size={20} />
                  </div>
                  <div>
                    <div className="font-medium">Crear con IA</div>
                    <div className="text-sm text-muted-foreground">Generar imagen personalizada con IA</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Image Upload Modal */}
        <ImageUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onUpload={handleUploadImage}
        />

        {/* Stock Image Modal */}
        <StockImageModal
          isOpen={showStockModal}
          onClose={() => setShowStockModal(false)}
          onSelect={handleStockImageSelect}
          initialQuery=""
          tags={[]}
        />

        {/* AI Image Modal */}
        <AIImageModal
          isOpen={showAIModal}
          onClose={() => setShowAIModal(false)}
          onGenerate={handleAIImageGenerate}
          title=""
        />

        {aiEditContext && (
          <AITextEditModal
            isOpen={showAITextModal}
            onClose={() => setShowAITextModal(false)}
            onApply={applyAITextEdit}
            fieldLabel={ARTICLE_FIELD_LABELS[articleField] || articleField}
            selectedText={aiEditContext.selectedText}
            hasSelection={aiEditContext.start !== aiEditContext.end}
            statisticTitle={statisticTitle}
            fullText={value}
            selectionStart={aiEditContext.start}
            selectionEnd={aiEditContext.end}
            articleField={articleField}
          />
        )}
      </div>
    );
  };

export default MarkdownEditor; 